// src/ui/components/ContactsModal.js
import { getSupabaseClient } from "../../services/supabaseClient.js";
import { getCurrentTeamId } from "../../services/teamService.js";
import { actions } from "../../app/state.js";
import {
  CONTACT_TYPE_LABELS,
  CONTACT_TYPE_OPTIONS,
  CONTACT_TYPES,
  SUPPLIER_TYPE_LABELS,
  SUPPLIER_TYPE_OPTIONS,
} from "../../domain/constants.js";
import { normalizeContactType, normalizeSupplierTypes } from "../../domain/mappers.js";
import { onSafeBackdropClick } from "../modalUtils.js";

const $ = (root, sel) => root.querySelector(sel);
const normStr = (v) => String(v ?? "").trim();

function closeSelf(root) {
  window.CRM?.modals?.closeModal?.(root);
}

function showErrors(root, messages) {
  const box = $(root, "#contact-modal-errors");
  const ul = $(root, "#contact-modal-errors-list");
  if (!box || !ul) return;

  if (!messages.length) {
    box.classList.add("hidden");
    ul.innerHTML = "";
    return;
  }

  ul.innerHTML = messages.map((m) => `<li>${m}</li>`).join("");
  box.classList.remove("hidden");
}

function setEditMode(root, isEditable) {
  const inputs = root.querySelectorAll("input, select, textarea");
  inputs.forEach((el) => {
    el.disabled = !isEditable;
    // Add visual feedback class
    if (!isEditable) el.classList.add("bg-slate-50", "cursor-not-allowed");
    else el.classList.remove("bg-slate-50", "cursor-not-allowed");
  });

  const saveBtn = $(root, "#contact-modal-save");
  if (saveBtn) saveBtn.classList.toggle("hidden", !isEditable);

  const deleteBtn = $(root, "#contact-modal-delete");
  const mode = $(root, "#contact-modal-mode").textContent;
  if (deleteBtn && mode === "edit") {
    deleteBtn.classList.toggle("hidden", !isEditable);
  }

  const editToggle = $(root, "#contact-modal-edit-toggle");
  if (editToggle) {
    editToggle.classList.toggle("hidden", isEditable);
  }

  updateSupplierFields(root);
}

async function requireSession() {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!data?.user) throw new Error("No hay sesión activa.");
  return data.user;
}

function escapeHtml(str) {
  return String(str ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function fillSelect(select, options, labels, { emptyLabel = null } = {}) {
  if (!select) return;
  select.innerHTML = emptyLabel ? `<option value="">${escapeHtml(emptyLabel)}</option>` : "";
  options.forEach((value) => {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = labels[value] || value;
    select.appendChild(opt);
  });
}

function fillContactEnums(root) {
  fillSelect($(root, "#contact-type"), CONTACT_TYPE_OPTIONS, CONTACT_TYPE_LABELS);
  fillSelect($(root, "#contact-supplier-type"), SUPPLIER_TYPE_OPTIONS, SUPPLIER_TYPE_LABELS);
  renderSupplierTypeDropdown(root);
  syncSupplierTypeDropdown(root);
}

function selectedSupplierTypeValues(root) {
  return Array.from($(root, "#contact-supplier-type")?.selectedOptions || []).map(option => option.value);
}

function syncSupplierTypeDropdown(root) {
  const selected = selectedSupplierTypeValues(root);
  const selectedSet = new Set(selected);
  const summary = $(root, "#contact-supplier-types-summary");
  const menu = $(root, "#contact-supplier-types-menu");

  root.querySelectorAll("[data-supplier-type-option]").forEach((button) => {
    const isSelected = selectedSet.has(button.dataset.supplierTypeOption);
    button.setAttribute("aria-pressed", isSelected ? "true" : "false");
    button.classList.toggle("bg-blue-50", isSelected);
    button.classList.toggle("text-blue-800", isSelected);
    button.classList.toggle("text-slate-700", !isSelected);
    button.querySelector("[data-supplier-type-check]")?.classList.toggle("hidden", !isSelected);
    button.querySelector("[data-supplier-type-empty]")?.classList.toggle("hidden", isSelected);
  });

  if (summary) {
    summary.textContent = selected.length
      ? selected.map(value => SUPPLIER_TYPE_LABELS[value] || value).join(" + ")
      : "Selecciona tipos";
    summary.classList.toggle("text-slate-400", !selected.length);
    summary.classList.toggle("text-slate-800", Boolean(selected.length));
  }

  if (!selected.length) menu?.classList.add("hidden");
}

function renderSupplierTypeDropdown(root) {
  const optionsEl = $(root, "#contact-supplier-types-options");
  if (!optionsEl) return;

  optionsEl.innerHTML = SUPPLIER_TYPE_OPTIONS.map(value => `
    <button type="button" data-supplier-type-option="${escapeHtml(value)}" aria-pressed="false"
      class="flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50">
      <span data-supplier-type-empty class="inline-flex h-4 w-4 shrink-0 rounded border border-slate-300 bg-white"></span>
      <span data-supplier-type-check class="hidden inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-blue-500 bg-blue-500 text-[10px] text-white">
        <i class="fa-solid fa-check"></i>
      </span>
      <span>${escapeHtml(SUPPLIER_TYPE_LABELS[value] || value)}</span>
    </button>
  `).join("");
}

function updateSupplierFields(root) {
  const isSupplierSelect = $(root, "#contact-is-supplier");
  const supplierField = $(root, "#contact-supplier-type-field");
  const supplierType = $(root, "#contact-supplier-type");
  const supplierToggle = $(root, "#contact-supplier-types-toggle");
  const supplierMenu = $(root, "#contact-supplier-types-menu");
  const nameLabel = $(root, "#contact-name-label");
  const companyLabel = $(root, "#contact-company-label");
  const isSupplier = isSupplierSelect?.value === "true";
  const disabled = !isSupplier || Boolean(isSupplierSelect?.disabled);

  root.querySelectorAll(".contact-supplier-only").forEach((el) => {
    el.classList.toggle("hidden", !isSupplier);
  });
  supplierField?.classList.toggle("hidden", !isSupplier);
  if (nameLabel) nameLabel.textContent = isSupplier ? "Persona de contacto" : "Nombre *";
  if (companyLabel) companyLabel.textContent = isSupplier ? "Empresa proveedora *" : "Empresa / Institucion";
  if (supplierType) {
    supplierType.disabled = disabled;
    if (!isSupplier) {
      Array.from(supplierType.options || []).forEach(option => {
        option.selected = false;
      });
      supplierMenu?.classList.add("hidden");
    }
  }
  if (supplierToggle) {
    supplierToggle.disabled = disabled;
    supplierToggle.classList.toggle("bg-slate-50", disabled);
    supplierToggle.classList.toggle("cursor-not-allowed", disabled);
  }
  root.querySelectorAll("[data-supplier-type-option]").forEach((button) => {
    button.disabled = disabled;
    button.classList.toggle("cursor-not-allowed", disabled);
    button.classList.toggle("opacity-60", disabled);
  });
  syncSupplierTypeDropdown(root);
}

function wireSupplierFields(root) {
  $(root, "#contact-is-supplier")?.addEventListener("change", () => updateSupplierFields(root));
  $(root, "#contact-supplier-types-toggle")?.addEventListener("click", () => {
    if ($(root, "#contact-supplier-types-toggle")?.disabled) return;
    $(root, "#contact-supplier-types-menu")?.classList.toggle("hidden");
  });
  $(root, "#contact-supplier-types-options")?.addEventListener("click", (ev) => {
    const button = ev.target.closest("[data-supplier-type-option]");
    if (!button || button.disabled) return;
    const value = button.dataset.supplierTypeOption;
    const option = Array.from($(root, "#contact-supplier-type")?.options || [])
      .find(item => item.value === value);
    if (option) option.selected = !option.selected;
    syncSupplierTypeDropdown(root);
  });
  root.addEventListener("mousedown", (ev) => {
    const dropdown = $(root, "#contact-supplier-types-dropdown");
    if (dropdown && !dropdown.contains(ev.target)) {
      $(root, "#contact-supplier-types-menu")?.classList.add("hidden");
    }
  });
}

function wireTabs(root) {
  const tabs = root.querySelectorAll("#contact-modal-tabs button");
  const tabContents = {
    datos: root.querySelector("#contact-modal-tab-datos"),
    eventos: root.querySelector("#contact-modal-tab-eventos"),
  };

  tabs.forEach(btn => {
    btn.addEventListener("click", () => {
      const target = btn.dataset.tab;

      tabs.forEach(t => {
        if (t === btn) {
          t.classList.add("border-blue-500", "text-blue-600");
          t.classList.remove("border-transparent", "text-slate-500", "hover:text-slate-700", "hover:border-slate-300");
        } else {
          t.classList.remove("border-blue-500", "text-blue-600");
          t.classList.add("border-transparent", "text-slate-500", "hover:text-slate-700", "hover:border-slate-300");
        }
      });

      Object.entries(tabContents).forEach(([key, contentEl]) => {
        if (!contentEl) return;
        if (key === target) {
          contentEl.classList.remove("hidden");
          contentEl.classList.add("block");
        } else {
          contentEl.classList.add("hidden");
          contentEl.classList.remove("block");
        }
      });
    });
  });

  // Init to 'datos'
  const firstBtn = root.querySelector('#contact-modal-tabs button[data-tab="datos"]');
  if (firstBtn) firstBtn.click();
}

function readForm(root) {
  const isSupplier = $(root, "#contact-is-supplier")?.value === "true";
  const company = normStr($(root, "#contact-company")?.value);
  const contactName = normStr($(root, "#contact-name")?.value);
  const selectedSupplierTypes = isSupplier
    ? normalizeSupplierTypes(Array.from($(root, "#contact-supplier-type")?.selectedOptions || []).map(option => option.value))
    : [];
  const values = {
    type: normalizeContactType($(root, "#contact-type")?.value || CONTACT_TYPES.OTHER),
    is_supplier: isSupplier,
    supplier_type: selectedSupplierTypes[0] || null,
    supplier_types: selectedSupplierTypes,
    name: isSupplier ? (contactName || company) : contactName,
    surname: normStr($(root, "#contact-surname")?.value) || null,
    company: company || null,
    email: normStr($(root, "#contact-email")?.value) || null,
    phone: normStr($(root, "#contact-phone")?.value) || null,
    notes: normStr($(root, "#contact-notes")?.value) || null,
  };

  if (isSupplier) {
    values.nif = normStr($(root, "#contact-nif")?.value) || null;
    values.fiscal_address = normStr($(root, "#contact-fiscal-address")?.value) || null;
    values.fiscal_city = normStr($(root, "#contact-fiscal-city")?.value) || null;
    values.fiscal_zip = normStr($(root, "#contact-fiscal-zip")?.value) || null;
  }

  return values;
}

function validate(values) {
  const errs = [];
  if (!values.type) errs.push("El tipo es obligatorio.");
  if (!values.name) errs.push("El nombre es obligatorio.");
  if (values.is_supplier && !values.supplier_types?.length) errs.push("Selecciona al menos un tipo de proveedor.");
  if (values.email && !values.email.includes("@")) errs.push("El email no es válido.");
  if (values.is_supplier && !values.company) errs.push("La empresa proveedora es obligatoria.");
  if (values.is_supplier && !values.email && !values.phone) errs.push("Indica un email o telefono del proveedor.");
  return errs;
}

// API pública
export async function openCreateContactModal({ onSaved } = {}) {
  await requireSession();
  const teamId = await getCurrentTeamId({ profileOptions: { force: true, allowNull: false } });

  const root = window.CRM.modals.openModal("contacts-modal-template");
  fillContactEnums(root);
  wireSupplierFields(root);

  // UI Initial State
  $(root, "#contact-modal-title").textContent = "Nuevo contacto";
  $(root, "#contact-modal-mode").textContent = "create";
  $(root, "#contact-modal-delete")?.classList.add("hidden");
  $(root, "#contact-modal-edit-toggle")?.classList.add("hidden");

  // Ocultar pestaña eventos al crear
  wireTabs(root);
  const eventosTab = root.querySelector('#contact-modal-tabs button[data-tab="eventos"]');
  if (eventosTab) eventosTab.classList.add("hidden");

  setEditMode(root, true);
  updateSupplierFields(root);

  // Wire buttons
  $(root, "#contact-modal-close")?.addEventListener("click", () => closeSelf(root));
  $(root, "#contact-modal-cancel")?.addEventListener("click", () => closeSelf(root));
  onSafeBackdropClick(root, () => closeSelf(root));

  $(root, "#contact-modal-save")?.addEventListener("click", async () => {
    const values = readForm(root);
    const errs = validate(values);
    showErrors(root, errs);
    if (errs.length) return;

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from("contacts")
        .insert({ ...values, team_id: teamId })
        .select("*")
        .single();

      if (error) throw error;

      actions.addToast?.({ type: "success", message: "Contacto creado" });
      closeSelf(root);
      onSaved?.(data);
    } catch (err) {
      actions.addToast?.({ type: "error", message: err.message });
    }
  });

  showErrors(root, []);
}

export async function openEditContactModal(contactId, { onSaved } = {}) {
  if (!contactId) return;
  await requireSession();
  const teamId = await getCurrentTeamId({ profileOptions: { force: true, allowNull: false } });

  const root = window.CRM.modals.openModal("contacts-modal-template");
  fillContactEnums(root);
  wireSupplierFields(root);

  // UI Initial State
  $(root, "#contact-modal-title").textContent = "Editar contacto";
  $(root, "#contact-modal-mode").textContent = "edit";
  $(root, "#contact-modal-id").textContent = contactId;

  // Wire buttons
  $(root, "#contact-modal-close")?.addEventListener("click", () => closeSelf(root));
  $(root, "#contact-modal-cancel")?.addEventListener("click", () => closeSelf(root));
  onSafeBackdropClick(root, () => closeSelf(root));

  // Start fully editable
  setEditMode(root, true);

  // Load data
  try {
    const supabase = getSupabaseClient();
    const { data: c, error } = await supabase.from("contacts").select("*").eq("id", contactId).single();
    if (error) throw error;

    $(root, "#contact-type").value = normalizeContactType(c.type || CONTACT_TYPES.OTHER);
    $(root, "#contact-is-supplier").value = c.is_supplier ? "true" : "false";
    const selectedSupplierTypes = normalizeSupplierTypes(c.supplier_types?.length ? c.supplier_types : [c.supplier_type].filter(Boolean));
    Array.from($(root, "#contact-supplier-type")?.options || []).forEach(option => {
      option.selected = selectedSupplierTypes.includes(option.value);
    });
    $(root, "#contact-name").value = c.name || "";
    $(root, "#contact-surname").value = c.surname || "";
    $(root, "#contact-company").value = c.company || "";
    $(root, "#contact-email").value = c.email || "";
    $(root, "#contact-phone").value = c.phone || "";
    $(root, "#contact-nif").value = c.nif || "";
    $(root, "#contact-fiscal-address").value = c.fiscal_address || "";
    $(root, "#contact-fiscal-city").value = c.fiscal_city || "";
    $(root, "#contact-fiscal-zip").value = c.fiscal_zip || "";
    $(root, "#contact-notes").value = c.notes || "";
    updateSupplierFields(root);

    // Activar pestañas
    wireTabs(root);
    const eventosTab = root.querySelector('#contact-modal-tabs button[data-tab="eventos"]');
    if (eventosTab) eventosTab.classList.remove("hidden");

    // Lógica para Historial de Eventos
    const evList = $(root, "#contact-modal-events-list");
    const countBadge = $(root, "#contact-modal-events-count");
    if (evList) {
      evList.innerHTML = "<li class='text-xs italic'>Cargando eventos...</li>";
      const { data: evData, error: evError } = await supabase
        .from("event_contacts")
        .select(`event_id, events ( id, client_name_legacy, date, stage )`)
        .eq("contact_id", contactId);

      if (evError) throw evError;

      if (!evData || evData.length === 0) {
        if (countBadge) countBadge.textContent = "0";
        evList.innerHTML = "<li class='text-xs italic px-1'>No hay eventos asociados a este contacto.</li>";
      } else {
        if (countBadge) countBadge.textContent = evData.length.toString();

        evList.innerHTML = evData.map(ev => {
          const e = ev.events;
          if (!e) return "";
          const dateStr = e.date ? new Date(e.date).toLocaleDateString("es-ES") : "Sin fecha";
          let stageColor = "bg-slate-100 text-slate-500";
          if (e.stage === "confirmed" || e.stage === "done") stageColor = "bg-emerald-100 text-emerald-700";
          if (e.stage === "rechazado") stageColor = "bg-red-100 text-red-700";

          return `<li class="activity-item flex items-center justify-between border border-slate-100 hover:border-slate-300 hover:bg-slate-50 hover:shadow-sm transition-all rounded-xl p-3 cursor-pointer mb-2 last:mb-0" data-event-id="${e.id}">
               <div class="flex-1 min-w-0 pr-2">
                 <div class="font-bold text-slate-800 truncate">${escapeHtml(e.client_name_legacy || 'Evento sin nombre')}</div>
                 <div class="text-xs text-slate-500 mt-0.5">${dateStr}</div>
               </div>
               <span class="text-[10px] whitespace-nowrap px-2 py-1 rounded-full font-bold uppercase tracking-tighter ${stageColor}">${e.stage || 'lead'}</span>
             </li>`;
        }).join("");

        // Click handler for opening the Event details
        evList.querySelectorAll(".activity-item").forEach(item => {
          item.addEventListener("click", () => {
            if (window.CRM?.modals?.openEditEventModal) {
              window.CRM.modals.openEditEventModal(item.dataset.eventId);
            } else {
              // Fallback literal import in case it's not registered globally
              import("./EventModal.js").then(module => {
                module.openEditEventModal(item.dataset.eventId);
              }).catch(err => {
                actions.addToast?.({ type: "error", message: "No se pudo abrir el evento." });
              });
            }
          });
        });
      }
    }

  } catch (err) {
    actions.addToast?.({ type: "error", message: err.message });
  }

  $(root, "#contact-modal-save")?.addEventListener("click", async () => {
    const values = readForm(root);
    const errs = validate(values);
    showErrors(root, errs);
    if (errs.length) return;

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from("contacts")
        .update(values)
        .eq("id", contactId)
        .select("*")
        .single();

      if (error) throw error;

      actions.addToast?.({ type: "success", message: "Contacto actualizado" });
      closeSelf(root);
      onSaved?.(data);
    } catch (err) {
      actions.addToast?.({ type: "error", message: err.message });
    }
  });

  $(root, "#contact-modal-delete")?.addEventListener("click", async () => {
    if (!confirm("¿Seguro que quieres eliminar este contacto?")) return;
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from("contacts").delete().eq("id", contactId);
      if (error) throw error;

      actions.addToast?.({ type: "success", message: "Contacto eliminado" });
      closeSelf(root);
      onSaved?.({ id: contactId, deleted: true });
    } catch (err) {
      actions.addToast?.({ type: "error", message: err.message });
    }
  });

  showErrors(root, []);
}

export default {
  openCreateContactModal,
  openEditContactModal,
};
